import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  BellIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [salesTrends, setSalesTrends] = useState(null);
  const [transactionBreakdown, setTransactionBreakdown] = useState(null);
  const [topProducts, setTopProducts] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [orderAging, setOrderAging] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    days: 30
  });

  useEffect(() => {
    if (!user) {
      navigate('/dashboard/login');
      return;
    }

    // Dashboard access for admin and employee roles
    // Note: finance and support roles can be added to database if needed
    const allowedRoles = ['admin', 'employee'];
    if (!allowedRoles.includes(user.role)) {
      navigate('/');
      return;
    }

    fetchDashboardData();
  }, [user, navigate, filters]);

  // Alerts (which include new-order notifications) poll on their own, separate from
  // the full dashboard fetch above - that pulls metrics/trends/top-products, which are
  // expensive queries with no reason to re-run every 20s just to catch a new order.
  useEffect(() => {
    if (!user) return;
    const fetchAlertsOnly = async () => {
      try {
        const token = getToken(true);
        const [alertsRes, agingRes] = await Promise.all([
          fetch('/api/dashboard/alerts', { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch('/api/dashboard/order-aging', { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (alertsRes.ok) {
          const data = await alertsRes.json();
          if (data.success) setAlerts(data.data?.alerts || []);
        }
        if (agingRes.ok) {
          const data = await agingRes.json();
          if (data.success) setOrderAging(data.data);
        }
      } catch (e) { /* ignore network errors */ }
    };
    fetchAlertsOnly();
    const interval = setInterval(fetchAlertsOnly, 20000);
    return () => clearInterval(interval);
  }, [user, getToken]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getToken(true); // Dashboard token
      const baseUrl = '/api/dashboard';

      // Fetch all data in parallel
      const [metricsRes, trendsRes, breakdownRes, productsRes, usersRes, alertsRes, activitiesRes] = await Promise.all([
        fetch(`${baseUrl}/metrics?start_date=${filters.start_date}&end_date=${filters.end_date}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/sales-trends?start_date=${filters.start_date}&end_date=${filters.end_date}&days=${filters.days}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/transaction-breakdown?start_date=${filters.start_date}&end_date=${filters.end_date}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/top-products?start_date=${filters.start_date}&end_date=${filters.end_date}&limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/active-users?start_date=${filters.start_date}&end_date=${filters.end_date}&days=${filters.days}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/alerts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${baseUrl}/recent-activities?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [metricsData, trendsData, breakdownData, productsData, usersResData, alertsData, activitiesData] = await Promise.all([
        metricsRes.json(),
        trendsRes.json(),
        breakdownRes.json(),
        productsRes.json(),
        usersRes.json(),
        alertsRes.json(),
        activitiesRes.json()
      ]);

      if (metricsData.success) setMetrics(metricsData.data);
      if (trendsData.success) setSalesTrends(trendsData.data);
      if (breakdownData.success) setTransactionBreakdown(breakdownData.data);
      if (productsData.success) setTopProducts(productsData.data);
      if (usersResData.success) setActiveUsers(usersResData.data);
      if (alertsData.success) setAlerts(alertsData.data.alerts || []);
      if (activitiesData.success) setRecentActivities(activitiesData.data.activities || []);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const baseMetrics = metrics?.base_metrics || {};
  const adminMetrics = metrics?.admin_metrics || {};
  const financeMetrics = metrics?.finance_metrics || {};
  const supportMetrics = metrics?.support_metrics || {};
  const loyalty = metrics?.loyalty || {};

  // KPI Cards based on user role
  const kpiCards = [];

  // Base KPIs for all roles
  kpiCards.push(
    {
      title: 'Total Sales',
      value: `₹${parseFloat(baseMetrics.total_sales || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: CurrencyDollarIcon,
      color: 'bg-green-500',
      change: '+12.5%',
      trend: 'up',
      clickable: true,
      onClick: () => navigate('/dashboard/orders')
    },
    {
      title: 'Transactions',
      value: baseMetrics.total_transactions || 0,
      icon: ShoppingCartIcon,
      color: 'bg-blue-500',
      change: '+8.2%',
      trend: 'up',
      clickable: true,
      onClick: () => navigate('/dashboard/orders')
    },
    {
      title: 'Active Users',
      value: baseMetrics.active_users || 0,
      icon: UserGroupIcon,
      color: 'bg-purple-500',
      change: '+15.3%',
      trend: 'up',
      clickable: true,
      onClick: () => navigate('/dashboard/customers')
    },
    {
      title: 'Loyalty Enrollments',
      value: loyalty.total_enrollments || 0,
      icon: ChartBarIcon,
      color: 'bg-yellow-500',
      change: `+${loyalty.enrollments_30d || 0} this month`,
      trend: 'up',
      clickable: true,
      onClick: () => navigate('/dashboard/customers')
    }
  );

  // Admin/Store Manager specific KPIs
  if (user?.role === 'admin' || user?.role === 'employee') {
    kpiCards.push(
      {
        title: 'Total Products',
        value: adminMetrics.total_products || 0,
        icon: ShoppingCartIcon,
        color: 'bg-indigo-500',
        clickable: true,
        onClick: () => navigate('/dashboard/products')
      },
      {
        title: 'Low Stock Items',
        value: adminMetrics.low_stock_items || 0,
        icon: ExclamationTriangleIcon,
        color: 'bg-orange-500',
        alert: adminMetrics.low_stock_items > 0,
        clickable: true,
        onClick: () => navigate('/dashboard/products?filter=low_stock')
      }
    );
  }

  // Finance specific KPIs
  if (user?.role === 'admin' || user?.role === 'finance') {
    kpiCards.push(
      {
        title: 'Total Revenue',
        value: `₹${parseFloat(financeMetrics.total_revenue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: CurrencyDollarIcon,
        color: 'bg-emerald-500',
        clickable: true,
        onClick: () => navigate('/dashboard/orders')
      },
      {
        title: 'Today\'s Sales',
        value: `₹${parseFloat(financeMetrics.today_sales_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        icon: CurrencyDollarIcon,
        color: 'bg-teal-500',
        clickable: true,
        onClick: () => navigate('/dashboard/orders')
      }
    );
  }

  // Support specific KPIs
  if (user?.role === 'admin' || user?.role === 'employee') {
    kpiCards.push(
      {
        title: 'Pending Tickets',
        value: supportMetrics.tickets_pending || 0,
        icon: BellIcon,
        color: 'bg-red-500',
        alert: supportMetrics.tickets_pending > 0,
        clickable: true,
        onClick: () => navigate('/dashboard/orders?status=PENDING')
      }
    );
  }

  return (
    <div className="space-y-6">
        {/* Header with Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
              <p className="text-gray-600 mt-1">Welcome back, {user?.first_name}!</p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={filters.days}
                onChange={(e) => {
                  const days = parseInt(e.target.value);
                  setFilters({
                    ...filters,
                    days,
                    start_date: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Alerts & Notifications</h2>
            <div className="space-y-2">
              {alerts.slice(0, 5).map((alert, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${
                    alert.type === 'error' ? 'bg-red-50 border-red-500' :
                    alert.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                    alert.type === 'success' ? 'bg-green-50 border-green-500' :
                    'bg-blue-50 border-blue-500'
                  } ${alert.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                  onClick={alert.link ? () => navigate(alert.link) : undefined}
                >
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className={`h-5 w-5 ${
                      alert.type === 'error' ? 'text-red-500' :
                      alert.type === 'warning' ? 'text-yellow-500' :
                      alert.type === 'success' ? 'text-green-500' :
                      'text-blue-500'
                    } mr-3 mt-0.5`} />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{alert.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Aging */}
        {orderAging && orderAging.counts.total > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Order Aging</h2>
              {orderAging.counts.delayed > 0 && (
                <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                  {orderAging.counts.delayed} delayed
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { key: 'today', label: 'Today' },
                { key: 'day1', label: '1 Day' },
                { key: 'day2', label: '2 Days' },
                { key: 'day3Plus', label: '3+ Days' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => navigate('/dashboard/orders?status=PENDING')}
                  className={`rounded-lg border p-4 text-left transition-shadow hover:shadow-md ${
                    key === 'day3Plus' && orderAging.counts[key] > 0
                      ? 'border-red-200 bg-red-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <p className="text-2xl font-bold text-gray-900">{orderAging.counts[key]}</p>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mt-1">{label}</p>
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {orderAging.counts.total} order{orderAging.counts.total === 1 ? '' : 's'} still need attention, based on how long each has been placed and how long it's sat in its current stage.
            </p>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {kpiCards.map((kpi, index) => {
            const Icon = kpi.icon;
            return (
              <div
                key={index}
                className={`bg-white rounded-lg shadow p-6 ${kpi.alert ? 'ring-2 ring-orange-500' : ''} ${kpi.clickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
                onClick={kpi.clickable && kpi.onClick ? kpi.onClick : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{kpi.value}</p>
                    {kpi.change && (
                      <div className={`flex items-center mt-2 text-sm ${
                        kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {kpi.trend === 'up' ? (
                          <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                        )}
                        {kpi.change}
                      </div>
                    )}
                  </div>
                  <div className={`${kpi.color} p-3 rounded-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Trends Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales Trends</h2>
            {salesTrends?.trends && salesTrends.trends.length > 0 ? (
              <div className="h-64 flex items-end justify-between space-x-1">
                {salesTrends.trends.map((trend, index) => {
                  const maxRevenue = Math.max(...salesTrends.trends.map(t => parseFloat(t.revenue || 0)));
                  const height = maxRevenue > 0 ? (parseFloat(trend.revenue || 0) / maxRevenue) * 100 : 0;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition cursor-pointer"
                        style={{ height: `${height}%` }}
                        title={`${new Date(trend.date).toLocaleDateString()}: ₹${parseFloat(trend.revenue || 0).toFixed(2)}`}
                      ></div>
                      <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                        {new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-12">No sales data available</p>
            )}
          </div>

          {/* Transaction Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Transaction Status</h2>
            {transactionBreakdown?.by_status && transactionBreakdown.by_status.length > 0 ? (
              <div className="space-y-4">
                {transactionBreakdown.by_status.map((status, index) => {
                  const total = transactionBreakdown.by_status.reduce((sum, s) => sum + (parseInt(s.count) || 0), 0);
                  const percentage = total > 0 ? ((parseInt(status.count) || 0) / total) * 100 : 0;
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 capitalize">{status.status}</span>
                        <span className="text-sm text-gray-900">{status.count} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-12">No transaction data available</p>
            )}
          </div>
        </div>

        {/* Top Products and Recent Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Products */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Products</h2>
            {topProducts?.products && topProducts.products.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sold</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topProducts.products.slice(0, 5).map((product) => (
                      <tr key={product.id} className="cursor-pointer hover:bg-gray-50" onClick={() => navigate('/dashboard/products')}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{product.total_quantity_sold || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          ₹{parseFloat(product.revenue || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-12">No product data available</p>
            )}
          </div>

          {/* Recent Activities */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h2>
            {recentActivities.length > 0 ? (
              <div className="space-y-4">
                {recentActivities.slice(0, 5).map((activity) => (
                  <div 
                    key={activity.id} 
                    className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    onClick={() => navigate(`/dashboard/orders/${activity.id}`)}
                  >
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <ShoppingCartIcon className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-sm text-gray-500">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      ₹{parseFloat(activity.amount || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-12">No recent activities</p>
            )}
          </div>
        </div>
      </div>
  );
};

export default Dashboard;

